import { useState, useEffect, useMemo } from 'react';
import { Table, Pagination, Container } from 'react-bootstrap';
import { Box, Button, Typography, Paper, Tooltip, IconButton, Chip } from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import './buildinginspection.scss';

const BuildingInspectionListTable = ({
  items = [],
  onEdit,
  onDelete,
  userRole = 'other',
  scrollThreshold = 768
}) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [currentPage, setCurrentPage] = useState(1);
  const sizePerPage = 10;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => new Date(b.inspection_date) - new Date(a.inspection_date));
  }, [items]);

  const totalPages = Math.ceil(sortedItems.length / sizePerPage);
  const paginatedItems = sortedItems.slice((currentPage - 1) * sizePerPage, currentPage * sizePerPage);

  const handleEditClick = (row, e) => {
    e.stopPropagation();
    onEdit(row);
  };

  const handleDeleteClick = (row, e) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete this inspection (${formatDate(row.inspection_date)})?`)) {
      onDelete(row);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPropertyTitle = (property_details) => {
    const property = property_details;
    const propertyInfo = property?.property_id;

    if (!propertyInfo) return 'Unknown Property';

    const title = propertyInfo.title || 'Unknown';
    const address = propertyInfo.address || '';
    const building = property?.building_name || '';

    return (
      <Tooltip
        title={
          <>
            <>{title}</><br />
            {building && <div>{building}</div>}
            {address && <div>{address}</div>}
          </>
        }
        arrow
      >
        <span>
          {title} <br />
          {building && (
            <small style={{ marginLeft: 4 }}>
              ({building})
            </small>
          )}
        </span>
      </Tooltip>
    );
  };

  const getSupervisedBy = (row) => {
    const completed = row.completed_by;
    if (!completed || !completed.supervisedByName) return 'Unknown';

    let roleDisplay = 'Unknown';
    if (completed.by?.role === 'technical staff') roleDisplay = 'Staff';
    else if (completed.by?.role === 'vendor') roleDisplay = 'Vendor';
    else if (completed.by?.role === 'manager') roleDisplay = 'Manager';
    else if (completed.by?.role === 'inspector') roleDisplay = 'Inspector';
    else if (completed.by?.role === 'company') roleDisplay = 'Company';

    return (
      <Tooltip title={`${completed.supervisedByName} • ${roleDisplay}`} arrow>
        <span>
          {completed.supervisedByName} <small>({roleDisplay})</small>
        </span>
      </Tooltip>
    );
  };

  const getAttachmentsCount = (row) => {
    const allAttachments = [
      ...(row.interior || []),
      ...(row.elevators || []),
      ...(row.underground || []),
      ...(row.outside || []),
      ...(row.misc_others || []),
    ].flatMap(item => item.attachments || []);
    const count = allAttachments.length;
    return count === 0 ? 'No Files' : `${count} File${count !== 1 ? 's' : ''}`;
  };

  const truncateText = (text, maxLength = 80) => {
    if (!text || text.trim() === '') return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const [year, month, day] = date.split('T')[0].split('-');
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPerformedAt = (date) => {
    if (!date) return 'N/A';

    return new Date(date).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getIssuesBreakdown = (row) => {
    const sections = {
      interior: 'Interior',
      elevators: 'Elevators',
      underground: 'Underground',
      outside: 'Outside',
      misc_others: 'Miscellaneous / Others',
    };

    const breakdown = [];
    let totalIssues = 0;

    Object.entries(sections).forEach(([key, label]) => {
      const items = row[key];
      if (!Array.isArray(items)) return;

      const openIssues = items.filter(item =>
        item?.needs_work === true &&
        item?.completed !== true
      );

      if (openIssues.length > 0) {
        breakdown.push({
          section: label,
          count: openIssues.length,
          items: openIssues.map(item => ({
            name: item.name || 'Unnamed',
            details: item.comments || 'Needs work',
            due_date: item.due_date,
            completed: item.completed,
          })),
        });
        totalIssues += openIssues.length;
      }
    });

    return { breakdown, totalIssues };
  };

  const getIssuesTooltipContent = (breakdown) => {
    if (breakdown.length === 0) {
      return <Typography variant="body2">No issues found - All inspections passed!</Typography>;
    }

    return (
      <Box sx={{ maxWidth: 400, maxHeight: 400, overflow: 'auto' }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.8rem',
            fontWeight: 500,
            mb: 1,
            pb: 1,
            borderBottom: '1px solid rgba(255,255,255,0.3)',
          }}
        >
          Issues Breakdown by Section
        </Typography>

        {breakdown.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center', mt: 0.5 }}>
            {breakdown.map((section, idx) => (
              <Chip
                key={idx}
                label={`${section.section}: ${section.count}`}
                size="small"
                sx={{
                  fontSize: '0.65rem',
                  height: '18px',
                  bgcolor: 'error.light',
                  color: 'white',
                  fontWeight: 'medium',
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const columns = [
    { key: 'property', text: 'Property' },
    { key: 'inspection_date', text: 'Inspection Date' },
    { key: 'updated_on', text: 'Updated On' },
    { key: 'completed_by', text: 'Completed By' },
    { key: 'issues', text: 'Issues' },
    { key: 'attachments', text: 'Attachments' },
    { key: 'conclusions', text: 'Conclusions' },
    { key: 'actions', text: 'Actions' },
  ];

  const NoDataIndication = () => (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" width="100%">
      <Box textAlign="center" py={4}>
        <FeatherIcon icon="home" size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
        <Typography variant="h6" color="text.secondary">
          No Building Inspections
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No inspection logs available for this property
        </Typography>
      </Box>
    </Box>
  );

  const shouldShowVerticalScrollbar = windowWidth <= scrollThreshold;

  const tableStyles = {
    minHeight: '50vh',
    maxHeight: '75vh',
    overflowY: shouldShowVerticalScrollbar ? 'auto' : 'visible',
    overflowX: 'auto',
    borderRadius: 2,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    bgcolor: 'background.paper',
    width: 'calc(100% - 30px)',
    minWidth: '800px',
    position: 'relative',
  };

  return (
    <Paper sx={tableStyles} className="building-inspection-table-container">
      <Container fluid className="table-container">
        <Table bordered hover responsive className="building-inspection-table">
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`${column.key === 'inspection_date' ? 'sticky-first-col' : ''} header-${column.key}`}
                >
                  {column.text}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedItems.length > 0 ? (
              paginatedItems.map((row) => {
                const { breakdown, totalIssues } = getIssuesBreakdown(row);

                return (
                  <tr
                    key={row._id}
                    onClick={() => onEdit(row)}
                    className="table-row"
                    style={{
                      cursor: 'pointer',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                    }}
                  >
                    {columns.map((column, colIndex) => {
                      let cellContent;
                      if (column.key === 'property') {
                        cellContent = getPropertyTitle(row.property_details);
                      }
                      else if (column.key === 'inspection_date') {
                        cellContent = formatDate(row.inspection_date);
                      }
                      else if (column.key === 'updated_on') {
                        cellContent = formatPerformedAt(row?.updatedAt);
                      }
                      else if (column.key === 'completed_by') {
                        cellContent = getSupervisedBy(row);
                      }
                      else if (column.key === 'issues') {
                        cellContent = (
                          <Tooltip
                            title={getIssuesTooltipContent(breakdown)}
                            arrow
                            placement="right"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: 'rgba(0, 0, 0, 0.9)',
                                  '& .MuiTooltip-arrow': {
                                    color: 'rgba(0, 0, 0, 0.9)',
                                  },
                                  p: 2,
                                  maxWidth: 'none'
                                }
                              }
                            }}
                          >
                            <Box
                              sx={{
                                display: 'inline-flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'help',
                                padding: '8px 12px',
                                borderRadius: 1,
                                transition: 'all 0.2s',
                                '&:hover': {
                                  bgcolor: totalIssues > 0 ? 'error.lighter' : 'success.lighter',
                                  transform: 'scale(1.05)',
                                }
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: totalIssues > 0 ? 'error.main' : 'success.main',
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                }}
                              >
                                {totalIssues}  {totalIssues === 0 ? 'All Clear' : `Item${totalIssues > 1 ? 's' : ''} Need Work`}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      }
                      else if (column.key === 'attachments') {
                        const countText = getAttachmentsCount(row);
                        const hasAttachments = [
                          'interior', 'elevators', 'underground', 'outside', 'misc_others'
                        ].some(section =>
                          Array.isArray(row[section]) &&
                          row[section].some(item => Array.isArray(item.attachments) && item.attachments.length > 0)
                        );

                        cellContent = (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 400,
                              color: hasAttachments ? 'primary.main' : 'text.secondary',
                            }}
                          >
                            {countText}
                          </Typography>
                        );
                      }
                      else if (column.key === 'conclusions') {
                        cellContent = (
                          <Tooltip title={truncateText(row.conclusions_recommendations, 200)} arrow>
                            <span style={{
                              display: 'block',
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {truncateText(row.conclusions_recommendations, 80) || 'N/A'}
                            </span>
                          </Tooltip>
                        );
                      }
                      else if (column.key === 'actions') {
                        cellContent = (
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                            <Tooltip title="Edit / View" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => handleEditClick(row, e)}
                                sx={{
                                  minWidth: 40,
                                  height: 25,
                                  borderRadius: 1,
                                  bgcolor: 'primary.main',
                                  color: '#fff',
                                  p: 0.5,
                                  '&:hover': {
                                    bgcolor: 'primary.dark',
                                  },
                                }}
                              >
                                <FeatherIcon icon="edit-2" size={16} />
                              </IconButton>
                            </Tooltip>

                            {userRole === 'company' && (
                              <Tooltip title="Delete Inspection" arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleDeleteClick(row, e)}
                                  sx={{
                                    minWidth: 40,
                                    height: 25,
                                    borderRadius: 1,
                                    bgcolor: 'warning.main',
                                    color: '#fff',
                                    p: 0.5,
                                    '&:hover': {
                                      bgcolor: 'warning.dark',
                                    },
                                  }}
                                >
                                  <FeatherIcon icon="trash-2" size={16} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        );
                      }

                      const cellClass = `${column.key === 'inspection_date' ? 'sticky-first-col' : ''} cell-${column.key}`;

                      return (
                        <td key={colIndex} className={cellClass}>
                          {cellContent}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  <NoDataIndication />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Container>

      {totalPages > 1 && (
        <div className="pagination-container">
          <Pagination>
            <Pagination.Prev
              onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {[...Array(totalPages)].map((_, index) => (
              <Pagination.Item
                key={index + 1}
                active={index + 1 === currentPage}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}
    </Paper>
  );
};

export { BuildingInspectionListTable };